import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, IconButton, Typography, Box,
  Table, TableHead, TableRow, TableCell, TableBody,
  Switch, Tooltip, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PercentIcon from '@mui/icons-material/Percent';
import { fetchData, postData, deleteData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';

const emptyRow = () => ({
  _key: Date.now() + Math.random(),
  Id: null,
  IsBasedOnQuantityOrAmount: true,  // true = Quantity, false = Amount
  CommissionRate: '',
  IsBasedOnPayment: false,
  DueDays: '',
  Remarks: '',
  IsActive: true,
});

const QtyAmtToggle = ({ value, onChange }) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    value={value ? 'qty' : 'amt'}
    onChange={(_, v) => { if (v !== null) onChange(v === 'qty'); }}
    sx={{
      height: '24px',
      '& .MuiToggleButton-root': {
        fontSize: '11px', fontWeight: '700', textTransform: 'none',
        padding: '0 8px', border: '1px solid #d0d0d0', lineHeight: 1,
        color: '#666', whiteSpace: 'nowrap',
        '&.Mui-selected': {
          color: 'white',
          backgroundColor: '#2C3E6E',
          borderColor: '#2C3E6E',
          '&:hover': { backgroundColor: '#1a2a4a' },
        },
      },
    }}
  >
    <ToggleButton value="qty">Qty</ToggleButton>
    <ToggleButton value="amt">Amt</ToggleButton>
  </ToggleButtonGroup>
);

const SalesExecutiveCommissionSlabModal = ({ open, onClose, onSave, item }) => {
  const { companyId, userId, token } = useAuth();
  const { logAudit } = useAuditLog();
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadRows = () => {
    if (!item) return;
    setLoading(true);
    setRows([]);
    fetchData('ExecutiveCommissionSlabApi', `/${companyId}/${item.Id}`, token)
      .then(res => {
        const existing = res?.responseCode === '1111' ? res.response_data : [];
        const mapped = existing.map(r => ({
          _key: r.Id,
          Id: r.Id,
          IsBasedOnQuantityOrAmount: r.IsBasedOnQuantityOrAmount ?? true,
          CommissionRate: r.CommissionRate ?? '',
          IsBasedOnPayment: r.IsBasedOnPayment ?? false,
          DueDays: r.DueDays ?? '',
          Remarks: r.Remarks ?? '',
          IsActive: r.IsActive ?? true,
        }));
        setRows(mapped.length ? mapped : [emptyRow()]);
      })
      .catch(() => setRows([emptyRow()]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !item) return;
    loadRows();
  }, [open, item]);

  const updateRow = (key, field, value) =>
    setRows(prev => prev.map(r => r._key === key ? { ...r, [field]: value } : r));

  const addRow = () => setRows(prev => [...prev, emptyRow()]);

  const handleDeleteRow = async (row) => {
    if (row.Id) {
      try { await deleteData('ExecutiveCommissionSlabApi', row.Id, token); } catch (e) { console.error(e); }
    }
    setRows(prev => prev.filter(r => r._key !== row._key));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payloads = rows.map(row => {
        const payload = {
          CategoryId: item.Id,
          IsBasedOnQuantityOrAmount: row.IsBasedOnQuantityOrAmount,
          CommissionRate: parseFloat(row.CommissionRate) || 0,
          IsBasedOnPayment: row.IsBasedOnPayment,
          DueDays: parseInt(row.DueDays) || 0,
          Remarks: row.Remarks || null,
          IsActive: row.IsActive,
          CompanyId: companyId,
        };
        if (row.Id) {
          payload.Id = row.Id;
          payload.ModifiedBy = userId;
          payload.ModifiedDate = new Date();
        } else {
          payload.CreatedBy = userId;
          payload.CreatedDate = new Date();
        }
        return payload;
      });
      await postData('ExecutiveCommissionSlabApi', '', payloads, token);
      logAudit(AUDIT_ACTION.CREATE, 'ExecutiveCommissionSlabApi', 0, null, { Count: payloads.length });
      onSave();
      loadRows();
    } catch (err) {
      console.error('Error saving commission slabs:', err);
    } finally {
      setSaving(false);
    }
  };

  const hCell = {
    color: 'white', fontSize: '11px', fontWeight: '600',
    padding: '6px 8px', whiteSpace: 'nowrap', letterSpacing: '0.3px'
  };
  const cell = { padding: '5px 6px', verticalAlign: 'middle' };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #2C3E6E 0%, #4a6fa5 100%)',
          padding: '2px',
        }
      }}>
      <Box style={{ background: 'white', borderRadius: '10px', overflow: 'hidden' }}>

        <DialogTitle style={{
          background: 'linear-gradient(135deg, #2C3E6E 0%, #4a6fa5 100%)',
          color: 'white', padding: '10px 14px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <PercentIcon style={{ fontSize: '18px' }} />
            <Box>
              <Typography style={{ fontWeight: '700', fontSize: '14px', lineHeight: 1.2 }}>
                Sales Executive Commission Slabs
              </Typography>
              <Typography style={{ fontSize: '11px', opacity: 0.85, lineHeight: 1.2 }}>
                {item?.Name}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} style={{ color: 'white', padding: '4px' }} size="small">
            <CloseIcon style={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        <DialogContent style={{ padding: '14px 14px 8px' }}>
          {loading ? (
            <Typography style={{ textAlign: 'center', padding: '30px', color: '#999', fontSize: '13px' }}>
              Loading slabs...
            </Typography>
          ) : (
            <>
              <Box style={{ overflowX: 'auto' }}>
                <Table size="small" style={{ minWidth: 600 }}>
                  <TableHead>
                    <TableRow style={{ background: '#2C3E6E' }}>
                      <TableCell style={{ ...hCell, textAlign: 'center', width: '90px' }}>Based On</TableCell>
                      <TableCell style={{ ...hCell, width: '100px' }}>Commission %</TableCell>
                      <TableCell style={{ ...hCell, textAlign: 'center', width: '100px' }}>On Payment</TableCell>
                      <TableCell style={{ ...hCell, width: '80px' }}>Due Days</TableCell>
                      <TableCell style={hCell}>Remarks</TableCell>
                      <TableCell style={{ ...hCell, textAlign: 'center', width: '55px' }}>Active</TableCell>
                      <TableCell style={{ ...hCell, width: '32px' }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, idx) => (
                      <TableRow key={row._key} style={{
                        background: idx % 2 === 0 ? '#fff' : '#fafafa',
                      }}>

                        <TableCell style={{ ...cell, textAlign: 'center' }}>
                          <QtyAmtToggle
                            value={row.IsBasedOnQuantityOrAmount}
                            onChange={v => updateRow(row._key, 'IsBasedOnQuantityOrAmount', v)}
                          />
                        </TableCell>

                        <TableCell style={cell}>
                          <TextField size="small" value={row.CommissionRate}
                            onChange={e => updateRow(row._key, 'CommissionRate', e.target.value)}
                            placeholder="0.00" fullWidth
                            inputProps={{ style: { textAlign: 'right' } }}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', height: '28px' } }}
                          />
                        </TableCell>

                        <TableCell style={{ ...cell, textAlign: 'center' }}>
                          <Switch size="small" checked={row.IsBasedOnPayment}
                            onChange={e => updateRow(row._key, 'IsBasedOnPayment', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#2C3E6E' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2C3E6E' },
                            }}
                          />
                        </TableCell>

                        <TableCell style={cell}>
                          <TextField size="small" value={row.DueDays}
                            onChange={e => updateRow(row._key, 'DueDays', e.target.value)}
                            placeholder="0" fullWidth
                            inputProps={{ style: { textAlign: 'right' } }}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', height: '28px' } }}
                          />
                        </TableCell>

                        <TableCell style={cell}>
                          <TextField size="small" value={row.Remarks}
                            onChange={e => updateRow(row._key, 'Remarks', e.target.value)}
                            placeholder="Optional" fullWidth
                            inputProps={{ maxLength: 150 }}
                            sx={{ '& .MuiOutlinedInput-root': { fontSize: '12px', height: '28px' } }}
                          />
                        </TableCell>

                        <TableCell style={{ ...cell, textAlign: 'center' }}>
                          <Switch size="small" checked={row.IsActive}
                            onChange={e => updateRow(row._key, 'IsActive', e.target.checked)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#2C3E6E' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2C3E6E' },
                            }}
                          />
                        </TableCell>

                        <TableCell style={cell}>
                          <Tooltip title="Remove row" arrow>
                            <IconButton size="small" onClick={() => handleDeleteRow(row)}
                              style={{ color: '#e53e3e', padding: '3px' }}>
                              <DeleteIcon style={{ fontSize: '15px' }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <Button
                startIcon={<AddIcon style={{ fontSize: '14px' }} />}
                onClick={addRow}
                size="small"
                style={{
                  marginTop: '10px', textTransform: 'none', fontSize: '12px',
                  color: '#2C3E6E', fontWeight: '600', padding: '4px 10px',
                  border: '1px dashed #2C3E6E', borderRadius: '6px',
                }}
              >
                Add Row
              </Button>
            </>
          )}
        </DialogContent>

        <DialogActions style={{
          padding: '8px 14px', background: '#f8f9fa',
          borderTop: '1px solid #e9ecef', gap: '8px'
        }}>
          <Typography style={{ fontSize: '11px', color: '#999', flex: 1 }}>
            {rows.length} slab{rows.length !== 1 ? 's' : ''} configured
          </Typography>
          <Button onClick={onClose}
            style={{
              color: '#6c757d', textTransform: 'none', fontWeight: '500',
              fontSize: '12px', padding: '4px 12px'
            }}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} variant="contained"
            style={{
              background: 'linear-gradient(135deg, #2C3E6E 0%, #4a6fa5 100%)',
              color: 'white', textTransform: 'none', fontWeight: '600',
              borderRadius: '6px', padding: '5px 16px', fontSize: '12px'
            }}>
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default SalesExecutiveCommissionSlabModal;

